import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasTableStyle, readTables } from "@/office/word/tables";

export const lektionTabellenformatvorlagen: Lesson = {
  id: "tabellenformatvorlagen",
  title: "Tabellen gestalten",
  summary: "Tabellenformatvorlagen statt Rahmen und Farben von Hand.",
  durationMinutes: 8,
  learningGoals: [
    "Tabellenformatvorlagen zuweisen",
    "Die Optionen für Kopfzeile, erste Spalte und Zeilenbänder nutzen",
    "Spaltenbreiten sinnvoll anpassen",
  ],
  steps: [
    {
      id: "vorlage-zuweisen",
      title: "Eine Tabellenformatvorlage zuweisen",
      explanation: [
        "Auch Tabellen haben Formatvorlagen – und aus demselben Grund wie Absätze: Statt " +
          "Rahmen, Hintergrundfarben und Schriftschnitte einzeln zu setzen, weist du ein " +
          "fertiges Paket zu und änderst es bei Bedarf an einer Stelle.",
        "Sobald der Cursor in der Tabelle steht, erscheint die Registerkarte " +
          "„Tabellenentwurf“ mit einem Katalog. Wie beim Formatvorlagenkatalog zeigt Word " +
          "eine Vorschau, wenn du mit der Maus darüberfährst.",
        "Für die meisten Dokumente reicht eine schlichte Vorlage mit hervorgehobener " +
          "Kopfzeile und dezenten Linien. Kräftige Farbflächen in jeder Zelle lenken vom " +
          "Inhalt ab und kosten beim Ausdruck unnötig Toner.",
      ],
      keyPoints: [
        "Registerkarte „Tabellenentwurf“ → Katalog der Tabellenformatvorlagen.",
        "Schlicht schlägt bunt – die Daten sollen im Vordergrund stehen.",
        "Die Vorlage lässt sich wie bei Absätzen nachträglich ändern.",
      ],
      task: {
        instruction:
          "Weise deiner Tabelle eine Tabellenformatvorlage zu – irgendeine außer der " +
          "schlichten Standardtabelle.",
        hint: "Cursor in die Tabelle setzen, dann im Katalog unter „Tabellenentwurf“ eine Vorlage wählen.",
        check: async () => {
          const tabellen = await readTables();
          if (tabellen.length === 0) {
            return fail("Im Dokument gibt es noch keine Tabelle.", "Lege zuerst eine Tabelle an.");
          }

          const tabelle = tabellen[0];
          if (!hasTableStyle(tabelle)) {
            return fail(
              `Der Tabelle ist noch keine Formatvorlage zugewiesen (erkannt: ${
                tabelle.styleName || tabelle.styleBuiltIn
              }).`,
              "Von Hand gesetzte Rahmen zählen nicht – es muss eine Vorlage aus dem Katalog sein.",
            );
          }
          return fromCriteria(
            [
              criterion(`Eine Tabellenformatvorlage ist zugewiesen („${tabelle.styleName}“)`, true),
              criterion(
                "Die Überschriftenzeile ist weiterhin gesetzt",
                tabelle.headerRowCount >= 1,
              ),
            ],
            {
              success: "Die Tabelle sieht jetzt einheitlich aus – und bleibt es auch.",
              failure: "Die Vorlage sitzt, aber die Überschriftenzeile ist verloren gegangen.",
              hint: "Setze sie über Layout → „Überschriften wiederholen“ erneut.",
            },
          );
        },
      },
    },
    {
      id: "vorlagenoptionen",
      title: "Die Optionen der Vorlage",
      explanation: [
        "Links neben dem Katalog stehen unter „Tabellenformatoptionen“ mehrere " +
          "Kontrollkästchen: Überschriftenzeile, Ergebniszeile, Verbundene Zeilen, Erste " +
          "Spalte, Letzte Spalte, Verbundene Spalten.",
        "Sie steuern, welche Teile der Tabelle die Vorlage besonders behandelt. " +
          "„Verbundene Zeilen“ erzeugt zum Beispiel abwechselnd eingefärbte Zeilen – bei " +
          "breiten Tabellen eine echte Lesehilfe, weil das Auge beim Wandern nach rechts " +
          "in der Zeile bleibt.",
        "„Erste Spalte“ hebt die linke Spalte hervor, was sich anbietet, wenn dort die " +
          "Bezeichnungen stehen. Wichtig: Diese Kästchen ändern nur das Aussehen. Die " +
          "eigentliche Überschriftenzeile aus der letzten Lektion ist eine andere " +
          "Einstellung und bleibt davon unberührt.",
      ],
      keyPoints: [
        "Tabellenentwurf → Tabellenformatoptionen.",
        "„Verbundene Zeilen“ hilft bei breiten Tabellen beim Lesen.",
        "Die Optionen ändern nur das Aussehen, nicht die Struktur.",
      ],
      task: {
        instruction:
          "Aktiviere für deine Tabelle die Option „Verbundene Zeilen“, damit sich die " +
          "Zeilen farblich abwechseln.",
        hint: "Registerkarte „Tabellenentwurf“, ganz links bei den Kontrollkästchen.",
        check: async () => {
          const tabellen = await readTables();
          if (tabellen.length === 0) {
            return fail("Im Dokument gibt es noch keine Tabelle.", "Lege zuerst eine Tabelle an.");
          }
          const tabelle = tabellen[0];
          return fromCriteria(
            [criterion("Die Option „Verbundene Zeilen“ ist aktiv", tabelle.bandedRows)],
            {
              success: "Die abwechselnden Zeilenfarben machen breite Tabellen deutlich lesbarer.",
              failure: "Die Option ist noch nicht aktiviert.",
              hint: "Das Kontrollkästchen heißt im deutschen Word „Verbundene Zeilen“ und sitzt unter „Tabellenentwurf“.",
            },
          );
        },
      },
    },
  ],
};
